import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import type { TrendBucket } from "./api";
import { formatBucketLabel, type TrendMatrix, type TrendSeries } from "./trend";

/**
 * Hand-rolled grouped vertical bar chart (F-023) - deliberately no charting
 * library. One bar per group per time bucket; recessive single y-axis with
 * integer ticks; per-bar HTML tooltip; legend always present; "Als Tabelle
 * anzeigen" swaps the SVG for an accessible table of the same data so series
 * identity is never color-alone (also the relief for low-contrast fills).
 *
 * Responsive (NF-020): the SVG renders at the measured container width, so
 * the chart compresses instead of forcing the page to scroll horizontally.
 */

const CHART_HEIGHT = 240;
const MARGIN = { top: 8, right: 8, bottom: 24, left: 36 } as const;
const MAX_BAR_WIDTH = 40;
const BAR_GAP = 2;

interface TrendChartProps {
  matrix: TrendMatrix;
  bucket: TrendBucket;
}

interface HoverState {
  seriesIndex: number;
  bucketIndex: number;
  /** Tooltip anchor in container coordinates (px). */
  left: number;
  top: number;
}

/** <= 5 integer y-ticks: step from the 1/2/5 ladder, yMax a step multiple. */
function integerTicks(maxValue: number): { yMax: number; ticks: number[] } {
  const max = Math.max(1, maxValue);
  let step = 1;
  for (let magnitude = 1; ; magnitude *= 10) {
    for (const base of [1, 2, 5]) {
      step = base * magnitude;
      if (max / step <= 5) {
        const yMax = Math.ceil(max / step) * step;
        const ticks: number[] = [];
        for (let v = 0; v <= yMax; v += step) ticks.push(v);
        return { yMax, ticks };
      }
    }
  }
}

/** Bar path with 4px rounded TOP corners only, flat at the baseline. */
function barPath(x: number, y: number, w: number, h: number): string {
  const r = Math.min(4, w / 2, h);
  const bottom = y + h;
  return [
    `M ${x} ${bottom}`,
    `L ${x} ${y + r}`,
    `Q ${x} ${y} ${x + r} ${y}`,
    `L ${x + w - r} ${y}`,
    `Q ${x + w} ${y} ${x + w} ${y + r}`,
    `L ${x + w} ${bottom}`,
    "Z",
  ].join(" ");
}

export function TrendChart({ matrix, bucket }: TrendChartProps): JSX.Element {
  const { t } = useTranslation();
  const [showTable, setShowTable] = useState(false);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState<HoverState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  /** Series display name; null-group and Sonstige resolve via i18n. */
  const seriesName = (series: TrendSeries): string =>
    series.isOthers
      ? t("dashboard.trend.others")
      : (series.name ?? t("dashboard.trend.unassigned"));

  const bucketLabels = useMemo(
    () => matrix.buckets.map((b) => formatBucketLabel(bucket, b)),
    [matrix.buckets, bucket],
  );

  if (matrix.buckets.length === 0 || matrix.series.length === 0) {
    return (
      <div
        data-testid="dashboard_trend-empty"
        className="flex h-48 items-center justify-center rounded-lg border border-dashed"
      >
        <p className="text-sm text-muted-foreground">
          {t("dashboard.trend.empty")}
        </p>
      </div>
    );
  }

  const nBuckets = matrix.buckets.length;
  const nSeries = matrix.series.length;
  const maxCount = Math.max(
    ...matrix.series.flatMap((series) => series.values),
  );
  const { yMax, ticks } = integerTicks(maxCount);

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = CHART_HEIGHT;
  const totalHeight = innerHeight + MARGIN.top + MARGIN.bottom;
  const bucketWidth = nBuckets > 0 ? innerWidth / nBuckets : 0;
  /** Per-bar column inside a bucket - also the tooltip hit-target width. */
  const columnWidth = nSeries > 0 ? bucketWidth / nSeries : 0;
  const barWidth = Math.min(
    MAX_BAR_WIDTH,
    Math.max(1, columnWidth - BAR_GAP),
  );
  const y = (value: number): number =>
    innerHeight - (value / yMax) * innerHeight;
  // Thin out x-labels when buckets get dense (~56px per label minimum).
  const labelEvery = Math.max(1, Math.ceil(nBuckets / (innerWidth / 56 || 1)));

  const hoverSeries = hover ? matrix.series[hover.seriesIndex] : null;

  return (
    <div data-testid="dashboard_trend-chart" className="space-y-3">
      {showTable ? (
        <div className="overflow-x-auto rounded-lg border">
          <Table data-testid="dashboard_trend-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("dashboard.trend.tableBucket")}</TableHead>
                {matrix.series.map((series) => (
                  <TableHead key={series.key} className="text-right">
                    {seriesName(series)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {matrix.buckets.map((_, bucketIdx) => (
                <TableRow
                  key={matrix.buckets[bucketIdx]}
                  data-testid={`dashboard_trend-row_${matrix.buckets[bucketIdx]}`}
                >
                  <TableCell className="font-medium">
                    {bucketLabels[bucketIdx]}
                  </TableCell>
                  {matrix.series.map((series) => (
                    <TableCell key={series.key} className="text-right">
                      {series.values[bucketIdx]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div
          ref={containerRef}
          data-testid="dashboard_trend-plot"
          className="relative w-full"
          onMouseLeave={() => setHover(null)}
        >
          <svg
            role="img"
            aria-label={t("dashboard.trend.chartLabel")}
            width="100%"
            height={totalHeight}
            viewBox={`0 0 ${Math.max(width, 1)} ${totalHeight}`}
            preserveAspectRatio="none"
          >
            <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
              {/* Recessive grid + y-axis: 1px light lines, muted text. */}
              {ticks.map((tick) => (
                <g key={tick}>
                  <line
                    x1={0}
                    x2={innerWidth}
                    y1={y(tick)}
                    y2={y(tick)}
                    className="stroke-border"
                    strokeWidth={1}
                  />
                  <text
                    x={-8}
                    y={y(tick)}
                    dy="0.32em"
                    textAnchor="end"
                    fontSize={10}
                    className="fill-muted-foreground"
                  >
                    {tick}
                  </text>
                </g>
              ))}

              {/* Bars: grouped per bucket, rounded tops, 2px gaps. */}
              {matrix.buckets.map((bucketStart, bucketIdx) => {
                const groupLeft = bucketIdx * bucketWidth;
                return (
                  <g key={bucketStart}>
                    {matrix.series.map((series, seriesIdx) => {
                      const value = series.values[bucketIdx] ?? 0;
                      const columnLeft = groupLeft + seriesIdx * columnWidth;
                      const barX =
                        columnLeft + (columnWidth - barWidth) / 2;
                      const barY = y(value);
                      const barHeight = innerHeight - barY;
                      return (
                        <g key={series.key}>
                          {value > 0 ? (
                            <path
                              d={barPath(barX, barY, barWidth, barHeight)}
                              fill={series.color}
                            />
                          ) : null}
                          {/* Hit target: the bar's full column, full height. */}
                          <rect
                            x={columnLeft}
                            y={0}
                            width={columnWidth}
                            height={innerHeight}
                            fill="transparent"
                            onMouseEnter={() =>
                              setHover({
                                seriesIndex: seriesIdx,
                                bucketIndex: bucketIdx,
                                left:
                                  MARGIN.left + columnLeft + columnWidth / 2,
                                top: MARGIN.top + (value > 0 ? barY : innerHeight) - 6,
                              })
                            }
                          />
                        </g>
                      );
                    })}
                  </g>
                );
              })}

              {/* X labels, thinned when dense. */}
              {bucketLabels.map((label, bucketIdx) =>
                bucketIdx % labelEvery === 0 ? (
                  <text
                    key={matrix.buckets[bucketIdx]}
                    x={bucketIdx * bucketWidth + bucketWidth / 2}
                    y={innerHeight + 14}
                    textAnchor="middle"
                    fontSize={10}
                    className="fill-muted-foreground"
                  >
                    {label}
                  </text>
                ) : null,
              )}
            </g>
          </svg>

          {hover && hoverSeries ? (
            <div
              data-testid="dashboard_trend-tooltip"
              className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-md border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-md"
              style={{
                left: Math.min(Math.max(hover.left, 48), Math.max(width - 48, 48)),
                top: Math.max(hover.top, 0),
              }}
            >
              <div className="flex items-center gap-1.5 font-medium">
                <span
                  aria-hidden="true"
                  className="inline-block h-2.5 w-2.5 rounded-[2px]"
                  style={{ backgroundColor: hoverSeries.color }}
                />
                {seriesName(hoverSeries)}
              </div>
              <div className="text-muted-foreground">
                {bucketLabels[hover.bucketIndex]}
              </div>
              <div>
                {t("dashboard.trend.countLabel")}:{" "}
                {hoverSeries.values[hover.bucketIndex]}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Legend: colored swatch + name in normal text color, never color-alone. */}
      <div data-testid="dashboard_legend" className="flex flex-wrap gap-x-4 gap-y-1.5">
        {matrix.series.map((series) => (
          <span
            key={series.key}
            data-testid={`dashboard_legend-entry_${series.key}`}
            className="inline-flex items-center gap-1.5 text-sm"
          >
            <span
              aria-hidden="true"
              className="inline-block h-2.5 w-2.5 rounded-[2px]"
              style={{ backgroundColor: series.color }}
            />
            {seriesName(series)}
          </span>
        ))}
      </div>

      <Button
        data-testid="dashboard_table-toggle"
        variant="outline"
        size="sm"
        aria-pressed={showTable}
        onClick={() => setShowTable((current) => !current)}
      >
        {showTable
          ? t("dashboard.trend.showChart")
          : t("dashboard.trend.showTable")}
      </Button>
    </div>
  );
}
